import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Search, UserPlus, X } from "lucide-react";
import {
  addNewPersonToTeam,
  searchPersonsForTeam,
  friendlyRegistrationError,
  type PersonSearchResult,
} from "@/api/registrations";
import { isVirtualEvent, type UpcomingEvent } from "@/api/eventDiscovery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GENDER_OPTIONS, type GenderValue } from "@/lib/gender";
import type { Gender } from "@/api/profile";
import { hasNonLatin } from "@/lib/latinName";
import { formatName } from "./types";
import { logger, serializeError } from "@/lib/logger";

interface Props {
  event: UpcomingEvent;
  excludeIds: number[];
  // Commits the add immediately (parent calls addTeamMember + reloads).
  onAdd: (p: PersonSearchResult) => void | Promise<void>;
  onCancel?: () => void;
}

export function AddMemberSearch({ event, excludeIds, onAdd, onCancel }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PersonSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const excludeSet = new Set(excludeIds);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchPersonsForTeam(event.event_id, query);
        if (!cancelled) setResults(data);
      } catch (err) {
        if (!cancelled) {
          logger.error("Search failed", serializeError(err));
          setResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, event.event_id]);

  const visible = results.filter((p) => !excludeSet.has(p.person_id));

  const handlePick = (p: PersonSearchResult) => {
    void onAdd(p);
    setQuery("");
    setResults([]);
  };

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" />
          Add a member
        </Label>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && onCancel) {
              e.preventDefault();
              onCancel();
            }
          }}
          className="pl-8"
        />
      </div>
      {searching ? (
        <div className="flex items-center text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
          Searching…
        </div>
      ) : query.trim().length >= 2 ? (
        <>
          {visible.length > 0 && (
            <ul className="max-h-48 overflow-y-auto divide-y rounded border bg-background">
              {visible.map((p) => (
                <li
                  key={p.person_id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{formatName(p)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.primary_email ?? "no email on file"}
                      {p.last_country ? ` · ${p.last_country}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePick(p)}
                  >
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <CreateAndAddForm
            event={event}
            onCreated={handlePick}
            hasResults={visible.length > 0}
            searchQuery={query.trim()}
          />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateAndAddForm — shown when search returns no matches
// ---------------------------------------------------------------------------

interface CreateAndAddFormProps {
  event: UpcomingEvent;
  onCreated: (p: PersonSearchResult) => void;
  hasResults?: boolean;
  searchQuery?: string;
}

function CreateAndAddForm({
  event,
  onCreated,
  hasResults = false,
  searchQuery = "",
}: CreateAndAddFormProps) {
  const parts = searchQuery.split(/\s+/);
  const [expanded, setExpanded] = useState(!hasResults);
  const [firstName, setFirstName] = useState(parts[0] ?? "");
  const [lastName, setLastName] = useState(parts.slice(1).join(" "));
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState<GenderValue>("unspecified");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const nameNonLatin = hasNonLatin(firstName) || hasNonLatin(lastName);
  const nameOk = !!firstName.trim() && !!lastName.trim() && !nameNonLatin;
  const genderOk = gender !== "unspecified";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameOk || !genderOk) {
      setShowErrors(true);
      return;
    }
    // genderOk narrows gender to a concrete value, no unsafe cast needed.
    const memberGender: Gender = gender;
    const trimFirst = firstName.trim();
    const trimLast = lastName.trim();
    setSubmitting(true);
    setError(null);
    try {
      // Location comes from the event; virtual events carry none, so the RPC
      // falls back to the caller's own home.
      const virtual = isVirtualEvent(event);
      const result = await addNewPersonToTeam(
        event.event_id,
        trimFirst,
        trimLast,
        email.trim() || undefined,
        memberGender,
        virtual ? null : event.city,
        virtual ? null : event.country,
      );
      onCreated({
        person_id: result.personId,
        first_name: trimFirst,
        last_name: trimLast,
        primary_email: email.trim() || null,
        last_country: virtual ? null : event.country,
      });
    } catch (err) {
      setError(friendlyRegistrationError(err, "Failed to add person"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2 rounded border bg-background px-3 py-2.5">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Not who you&apos;re looking for? Create a new person
        </button>
      ) : (
        <>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <UserPlus className="h-3.5 w-3.5" />
            {hasResults
              ? "Create a new person and add them:"
              : "No matches \u2014 create a new person and add them:"}
          </p>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="First name *"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-8 text-sm"
                required
              />
              <Input
                placeholder="Last name *"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-8 text-sm"
                required
              />
            </div>
            {showErrors && (!firstName.trim() || !lastName.trim()) ? (
              <p className="text-xs text-destructive">
                First and last name are required.
              </p>
            ) : null}
            {nameNonLatin ? (
              <p className="text-xs text-destructive">
                Please enter the name in Latin letters — non-Latin names
                can&apos;t be saved.
              </p>
            ) : null}
            <div>
              <select
                aria-label="Gender"
                value={gender}
                onChange={(e) => setGender(e.target.value as GenderValue)}
                className={`flex h-8 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                  gender === "unspecified" ? "text-muted-foreground" : ""
                } ${
                  showErrors && !genderOk
                    ? "border-destructive focus:ring-destructive"
                    : "border-input"
                }`}
              >
                <option value="unspecified" disabled>
                  Gender *
                </option>
                {GENDER_OPTIONS.filter(
                  (opt) => opt.value !== "unspecified",
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {showErrors && !genderOk ? (
                <p className="mt-1 text-xs text-destructive">
                  Please select their gender.
                </p>
              ) : null}
            </div>
            <Input
              type="email"
              placeholder="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-[11px] text-muted-foreground leading-tight">
              You can skip the email for now — ask your host or admin to add it
              later, or if your teammate registers themselves we'll merge the
              accounts.
            </p>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              disabled={submitting}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Adding…
                </>
              ) : (
                "Add to team"
              )}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
